'use client';

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import { Link, useRouter } from '@/i18n/routing';
import {
  Camera,
  ScanLine,
  AlertCircle,
  CheckCircle2,
  Clock,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { IS_QR_SCANNER_ENABLED } from '@/lib/feature-flags';
import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import { useHardwareScanner } from '@/hooks/use-hardware-scanner';
import { QrScanDialog } from '@/components/qr-scan-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import { clientsApi, clientKeys, type Client } from '@/features/clients';
import { devicesApi, deviceKeys, type Device } from '@/features/devices';
import { QR_REVIEW_HASH } from '../constants';
import { qrReviewQueries } from '../services/qr-review.queries';
import { QrReviewTableActionBar } from './qr-review-table-action-bar';
import { QrReviewCard } from './qr-review-card';
import { QrReviewCompactRow } from './qr-review-compact-row';
import type { QaStatus, QrReviewBatch, QrReviewListParams } from '../types';

// ─── Metric cards ─────────────────────────────────────────────────────────────

function MetricCards() {
  const t = useTranslations('qrReview');
  const { data } = useQuery(qrReviewQueries.metrics());
  const metrics = data?.data;

  const cards = [
    {
      label: t('metricActiveBatches'),
      value: metrics?.active_batches ?? 0,
      icon: Activity,
      accent: 'border-s-blue-500',
      iconClass: 'text-blue-500'
    },
    {
      label: t('metricPending'),
      value: metrics?.pending_batches ?? 0,
      icon: Clock,
      accent: 'border-s-amber-500',
      iconClass: 'text-amber-500'
    },
    {
      label: t('metricCompleted'),
      value: metrics?.completed_batches ?? 0,
      icon: CheckCircle2,
      accent: 'border-s-green-500',
      iconClass: 'text-green-500'
    }
  ];

  return (
    <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
      {cards.map((card) => (
        <div
          key={card.label}
          className={cn(
            'bg-card flex items-center gap-4 rounded-lg border border-s-4 p-4 shadow-sm',
            card.accent
          )}
        >
          <card.icon className={cn('h-8 w-8 shrink-0', card.iconClass)} />
          <div>
            <p className='text-muted-foreground text-xs'>{card.label}</p>
            <p className='text-2xl font-bold tabular-nums'>{card.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_BADGE_CLASSES: Record<QaStatus, string> = {
  in_qa: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  pending_qa:
    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  qa_passed:
    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  qa_failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
};

const STATUS_LABEL_KEYS: Record<QaStatus, string> = {
  in_qa: 'statusInQa',
  pending_qa: 'statusPendingQa',
  qa_passed: 'statusQaPassed',
  qa_failed: 'statusQaFailed'
};

function QaStatusBadge({ status }: { status: QaStatus }) {
  const t = useTranslations('qrReview');
  return (
    <Badge
      variant='outline'
      className={cn(
        'border-0 text-xs font-medium',
        STATUS_BADGE_CLASSES[status] ?? 'bg-muted text-muted-foreground'
      )}
    >
      {t(STATUS_LABEL_KEYS[status] ?? status)}
    </Badge>
  );
}

// ─── Main listing component ───────────────────────────────────────────────────

export function QrReviewListing() {
  const t = useTranslations('qrReview');
  const tc = useTranslations('common');
  const router = useRouter();
  const [scannerActive, setScannerActive] = useState(true);
  // Camera scan dialog open-state lives in the URL hash (#scan) so it is
  // deep-linkable and closes on browser Back.
  const { open: scanDialogOpen, onOpenChange: toggleScanDialog } =
    useHashDialog(QR_REVIEW_HASH.scan);
  const [hwScan, setHwScan] = useState<{ value: string; key: number } | null>(
    null
  );
  const [view, setView] = useDataView('qr-review');

  const mapClientOption = useCallback(
    (c: Client) => ({ value: String(c.id), label: c.name }),
    []
  );
  const mapDeviceOption = useCallback(
    (d: Device) => ({
      value: String(d.id),
      label: d.serial_number ?? d.name
    }),
    []
  );

  const clientInfinite = useInfiniteSelect<Client>({
    queryKey: clientKeys.lists(),
    queryFn: (params) => clientsApi.getAll(params),
    mapOption: mapClientOption
  });

  const deviceInfinite = useInfiniteSelect<Device>({
    queryKey: deviceKeys.lists(),
    queryFn: (params) => devicesApi.getAll(params),
    mapOption: mapDeviceOption
  });

  const { data: hwScanData, isFetching: hwScanFetching } = useQuery({
    ...qrReviewQueries.list({ search: hwScan?.value ?? '', per_page: 1 }),
    enabled: !!hwScan
  });

  useEffect(() => {
    if (!hwScan || hwScanFetching) return;
    const results = (hwScanData?.data ?? []) as QrReviewBatch[];
    if (results.length > 0) {
      router.push(`/dashboard/qr-review/${results[0].id}`);
    } else {
      toast.error(t('notFound'));
    }
  }, [hwScanData, hwScanFetching, hwScan, router, t]);

  useHardwareScanner({
    onScan: (value) => setHwScan({ value, key: Date.now() }),
    enabled: scannerActive
  });

  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState(
    'container_id',
    parseAsString.withDefault('')
  );
  const [urlQaStatus] = useQueryState(
    'qa_status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlClient] = useQueryState(
    'client_name',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlDevice] = useQueryState(
    'scanner_id',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );

  const queryParams = useMemo<QrReviewListParams>(() => {
    const params: QrReviewListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlSearch) params.search = urlSearch;
    if (urlQaStatus.length > 0) params.qa_status = urlQaStatus;
    if (urlClient.length > 0) {
      const ids = urlClient.map(Number).filter((n) => n > 0);
      if (ids.length > 0) params.client_id = ids;
    }
    if (urlDevice.length > 0) {
      const ids = urlDevice.map(Number).filter((n) => n > 0);
      if (ids.length > 0) params.device_id = ids;
    }
    return params;
  }, [urlPage, urlPerPage, urlSearch, urlQaStatus, urlClient, urlDevice]);

  const { data, isPending, isPlaceholderData } = useQuery(
    qrReviewQueries.list(queryParams)
  );

  const batches = useMemo(
    () => (data?.data ?? []) as QrReviewBatch[],
    [data?.data]
  );
  const pagination = data?.pagination;
  const pageCount = pagination?.totalPages ?? 0;

  const columns = useMemo<ColumnDef<QrReviewBatch>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40
      },
      {
        id: 'container_id',
        accessorKey: 'container_id',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('columnContainerId')}
          />
        ),
        cell: ({ row, column }) => {
          const value = row.original.container_id;
          return (
            <Link
              href={`/dashboard/qr-review/${row.original.id}`}
              className='font-semibold text-blue-600 hover:underline dark:text-blue-400'
            >
              {value ? (
                <HighlightMatch
                  text={String(value)}
                  query={getColumnSearchTerms(column)}
                />
              ) : (
                '—'
              )}
            </Link>
          );
        },
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: t('searchPlaceholder'),
          variant: 'text' as const,
          placeholder: t('searchPlaceholder'),
          permission: 'qr_review.column.columnContainerId'
        }
      },
      {
        id: 'client_name',
        accessorKey: 'client_name',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('columnClientName')}
          />
        ),
        cell: ({ row }) => (
          <span className='text-foreground'>
            {row.original.client_name || '—'}
          </span>
        ),
        enableSorting: false,
        // enableColumnFilter: true,
        meta: {
          label: t('columnClientName'),
          variant: 'infiniteMultiSelect' as const,
          permission: 'qr_review.column.columnClientName'
        }
      },
      {
        id: 'qa_status',
        accessorKey: 'qa_status',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('columnStatus')} />
        ),
        cell: ({ row }) => <QaStatusBadge status={row.original.qa_status} />,
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: t('columnStatus'),
          variant: 'multiSelect' as const,
          options: [
            { value: 'pending_qa', label: t('statusPendingQa') },
            { value: 'in_qa', label: t('statusInQa') },
            { value: 'qa_passed', label: t('statusQaPassed') },
            { value: 'qa_failed', label: t('statusQaFailed') }
          ],
          permission: 'qr_review.column.columnStatus'
        }
      },
      {
        id: 'page_count',
        accessorKey: 'total_qa_pages',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('columnPageCount')} />
        ),
        cell: ({ row }) => (
          <span className='text-muted-foreground tabular-nums'>
            {row.original.total_qa_pages} {t('pageSuffix')}
          </span>
        ),
        enableSorting: false,
        meta: {
          label: t('columnPageCount'),
          permission: 'qr_review.column.columnPageCount'
        }
      },
      {
        id: 'scanner_id',
        accessorKey: 'scanner_id',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('columnScannerId')} />
        ),
        cell: ({ row }) => (
          <span className='text-muted-foreground font-mono'>
            {row.original.scanner_id || '—'}
          </span>
        ),
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: t('columnScannerId'),
          variant: 'infiniteMultiSelect' as const,
          permission: 'qr_review.column.columnScannerId'
        }
      },
      {
        id: 'actions',
        header: () => <div className='text-end'>{t('columnActions')}</div>,
        cell: ({ row }) => (
          <div className='text-end'>
            <Button
              size='sm'
              variant='outline'
              onClick={() =>
                router.push(`/dashboard/qr-review/${row.original.id}`)
              }
            >
              {t('openForReview')}
            </Button>
          </div>
        ),
        enableSorting: false,
        enableHiding: false
      }
    ],
    [t, router]
  );

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<QrReviewBatch>({
    columns: filteredColumns,
    pageKey: 'qr-review',
    data: batches,
    pageCount,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('sortBatchNumber'), value: 'batch_number' },
      { label: t('sortContainerId'), value: 'container_id' },
      { label: t('sortClientName'), value: 'client_name' },
      { label: t('sortTotalQaPages'), value: 'total_qa_pages' }
    ],
    [t]
  );

  return (
    <div className='space-y-6'>
      {/* Metric cards */}
      {/* <MetricCards /> */}

      {/* Header row: view toggle + scan buttons */}
      <div className='flex items-center justify-end gap-2'>
        {view !== 'table' && (
          <SortControl
            options={sortOptions}
            sorting={tableState.sorting}
            onSortingChange={(s) => table.setSorting(s)}
          />
        )}
        <DataViewToggle
          value={view}
          onValueChange={setView}
          pageKey='qr-review'
        />
        {IS_QR_SCANNER_ENABLED && (
          <>
            <Button
              variant='outline'
              onClick={() => {
                setScannerActive((prev) => {
                  if (!prev) (document.activeElement as HTMLElement)?.blur();
                  return !prev;
                });
              }}
            >
              <span className='relative me-2 flex h-2 w-2 shrink-0'>
                <span
                  className={cn(
                    'absolute inline-flex h-full w-full rounded-full opacity-75',
                    scannerActive ? 'animate-ping bg-green-400' : 'bg-gray-400'
                  )}
                />
                <span
                  className={cn(
                    'relative inline-flex h-2 w-2 rounded-full',
                    scannerActive ? 'bg-green-500' : 'bg-gray-400'
                  )}
                />
              </span>
              <ScanLine className='me-2 h-4 w-4' />
              {t('scanQR')}
            </Button>
            <Button
              variant='outline'
              size='icon'
              onClick={() => toggleScanDialog(true)}
              title={tc('scanCamera')}
            >
              <Camera className='h-4 w-4' />
            </Button>
          </>
        )}
      </div>

      {/* Filters toolbar */}
      <DataTableToolbar
        table={table}
        viewFilterPermission='qr_review.view_filter'
        infiniteConfigs={{
          client_name: clientInfinite,
          scanner_id: deviceInfinite
        }}
      />

      {/* Data */}
      {isPending ? (
        <ListingSkeleton view={view} />
      ) : batches.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-16 text-center'>
          <AlertCircle className='text-muted-foreground mb-3 h-10 w-10' />
          <p className='text-muted-foreground'>{t('noResultsFound')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {batches.map((batch) => (
              <QrReviewCard key={batch.id} batch={batch} />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {batches.map((batch) => (
              <QrReviewCompactRow key={batch.id} batch={batch} />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <DataTable
            table={table}
            actionBar={<QrReviewTableActionBar table={table} />}
          />
        </div>
      )}

      <QrScanDialog
        open={scanDialogOpen}
        onOpenChange={toggleScanDialog}
        stage='qr-review'
        onScanValue={(value) => {
          setHwScan({ value, key: Date.now() });
          toggleScanDialog(false);
        }}
        translationNamespace='qrReview'
      />
    </div>
  );
}
